import React, { Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../Components/Spinner";

// Services
import { Accordion, Modal } from "react-bootstrap";
import { AiOutlineSend } from "react-icons/ai";
import { vehicleService } from "../../Services/vehicle.service";
import PositionsMap from "../Components/Maps/PositionsMap";
import PaginatedTableFleetCommands from "../Components/Tables/PaginatedTableFleetCommands";

import { useTranslation } from "react-i18next";
import "../../styles/fleet.css";
import { ToastComponent } from "../Components/ToastComponent";
import { TooltipIcon } from "../Components/Tooltips/TooltipInfos";

// Components lazy
const PaginatedTableFleet = React.lazy(() =>
  import("../Components/Tables/Customs/PaginatedTableFleet")
);

const PaginatedTableFleetPositions = React.lazy(() =>
  import("../Components/Tables/Customs/PaginatedTableFleetPositions")
);
const GoogleMapComponent = React.lazy(() =>
  import("../Components/Maps/GoogleMapComponent")
);
const GoogleMapTrackComponent = React.lazy(() =>
  import("../Components/Maps/GoogleMapTrackComponent")
);

export default function Fleet() {
  const { t } = useTranslation();

  const { vehicleSelected } = useSelector(
    (rootReducer) => rootReducer.vehicleSelectedReducer
  );

  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("gridAndMap");
  const [dataLoading, setDataLoading] = useState(true);

  // Polling
  const intervalIdRef = useRef(null); // Referencia do intervalo de polling
  const lastReceivedRef = useRef(null); // mantém o último lastReceived
  const currentGridPageRef = useRef(1); // mantém a página atual do grid
  const [delay] = useState(process.env.REACT_APP_UPDATE_FLEET_DATA_TIMER);

  // Grids
  const [paginatedTrackingList, setPaginatedTrackingList] = useState([]);
  const [gridPaginationMeta, setGridPaginationMeta] = useState({
    totalLines: 0,
    totalPages: 1,
    pageSize: 10,
    pageNumber: 1,
  });

  const [locationsTrackingList, setLocationsTrackingList] = useState([]);
  const [underMaintenace, setUnderMaintenace] = useState("");

  // Maps
  const [createMap, setCreateMap] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [startInterval, setStartInterval] = useState(true);
  const [onlyPositionEvents, setOnlyPositionEvents] = useState(false);

  // Trackings
  const [showTrackEvents, setShowTrackEvents] = useState(false);
  const [vehicleToTrack, setVehicleToTrack] = useState(null);

  const [diaryPositions, setDiaryPositions] = useState(null);

  const [activeAccordionKeys, setActiveAccordionKeys] = useState(["0", "1"]);

  const [handleClick, setHandleClick] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [startCommandsDate, setStartCommandsDate] = useState(null);
  const [endCommandsDate, setEndCommandsDate] = useState(null);

  const [showAvailableCommandsModal, setShowAvailableCommandsModal] =
    useState(false);
  const [availableCommands, setAvailableCommands] = useState([{}]);
  const [vehicleIdToCommands, setVehicleIdToCommands] = useState(0);

  const [toastParams, setToastParams] = useState("");

  const handleSetCommandDates = (start, end) => {
    setStartCommandsDate(start);
    setEndCommandsDate(end);
  };

  // rotinas para atualizar apenas dados novos
  const updateSmartVehiclesList = (prevList, newList) => {
    let changed = false;

    const map = new Map(prevList.map((v) => [v.id, v]));
    const result = newList.map((newItem) => {
      const oldItem = map.get(newItem.id);
      if (!oldItem || oldItem.generatedAt !== newItem.generatedAt) {
        changed = true;
        return newItem;
      }
      return oldItem;
    });

    if (!changed) {
      return prevList; // <- retorna o mesmo array, sem trigger de render
    }

    return result;
  };

  // Buscar registros de frotas (GRID) e posições
  const fetchTrackingList = async (selectedVehicleId, showLoading, pageNumber) => {
    if (showLoading) setDataLoading(true);

    const targetPage = pageNumber ?? currentGridPageRef.current;
    const lastReceived = lastReceivedRef.current
      ? new Date(lastReceivedRef.current).toISOString()
      : undefined;

    let gridData = null;
    let mapData = null;
    try {
      [gridData, mapData] = await Promise.all([
        vehicleService.fetchTrackVehiclesAll(selectedVehicleId || 0, undefined, targetPage, 10, lastReceived),
        vehicleService.fetchTrackVehiclesAll(selectedVehicleId || 0, undefined, 0, 0, lastReceived),
      ]);

      // Se qualquer uma das chamadas falhar, tenta novamente após 15s
      if (gridData === null || mapData === null) {
        setDataLoading(true);
        setTimeout(() => {
          fetchTrackingList(selectedVehicleId, true);
        }, 15000);
        return;
      }

      if (
        Array.isArray(gridData?.vehiclesUser) &&
        Array.isArray(mapData?.vehiclesUser)
      ) {
        const currentLastReceived = lastReceivedRef.current ?? 0;
        const newLastReceived = new Date(mapData.lastReceived).getTime();

        if (!currentLastReceived) {
          setPaginatedTrackingList(gridData);
          setLocationsTrackingList(mapData);
          lastReceivedRef.current = mapData.lastReceived; // armazena como string ISO
          setGridPaginationMeta({
            totalLines: gridData.totalLines || 0,
            totalPages: gridData.totalPages || 1,
            pageSize: gridData.pageSize || 10,
            pageNumber: gridData.pageNumber || 1,
          });
          setDataLoading(false);
          return;
        }

        if (newLastReceived > new Date(currentLastReceived).getTime()) {
          setCreateMap(false);
          console.log("🆕 Nova atualização:", newLastReceived);
          lastReceivedRef.current = mapData.lastReceived; // armazena como string ISO
          setGridPaginationMeta({
            totalLines: gridData.totalLines || 0,
            totalPages: gridData.totalPages || 1,
            pageSize: gridData.pageSize || 10,
            pageNumber: gridData.pageNumber || 1,
          });

          setPaginatedTrackingList((prev) => {
            const prevVehicles = Array.isArray(prev?.vehiclesUser)
              ? prev.vehiclesUser
              : [];

            const updatedVehicles = updateSmartVehiclesList(
              prevVehicles,
              gridData.vehiclesUser
            );

            return {
              ...prev,
              ...gridData,
              vehiclesUser: updatedVehicles,
            };
          });

          setLocationsTrackingList((prev) => {
            const prevVehicles = Array.isArray(prev?.vehiclesUser)
              ? prev.vehiclesUser
              : [];

            const updatedVehicles = updateSmartVehiclesList(
              prevVehicles,
              mapData.vehiclesUser
            );

            return {
              ...prev,
              ...mapData,
              vehiclesUser: updatedVehicles,
            };
          });
        } else {
          console.log("📭 Nenhuma nova atualização.");
        }
      } else {
        console.warn("❌ Resultado inválido");
        setPaginatedTrackingList([]);
      }

      return mapData;
    } finally {
      if (showLoading && mapData !== null) {
        setTimeout(() => {
          setDataLoading(false);
        }, 2000);
      }
    }
  };

  // ao selecionar, mudar de area (tab) entre, Grid, Mapa, Posições.
  const handleActiveTab = async (activeTab, selectedVehicle) => {
    setActiveTab(activeTab);
    // Se a aba ativa for "table", reseta o filtro e o combo para o primeiro item
    if (activeTab === "gridAndMap") {
      // Aguarda o dispatch refletir no estado antes de buscar
      setTimeout(async () => {
        dispatch({ type: "activeTab", value: activeTab });
        chooseDisplay(activeTab, 0);
      }, 100);
    } else {
      // Caso contrário, apenas altera a aba ativa e chama a função de exibição normalmente
      dispatch({ type: "activeTab", value: activeTab });
      chooseDisplay(
        activeTab,
        selectedVehicle?.vehicleId || vehicleSelected?.vehicleId
      );
    }
  };

  const triggerClick = (plate) => {
    const index = paginatedTrackingList.vehiclesUser.findIndex(
      (item) => item.plate === plate
    );

    if (index !== -1) {
      setSelectedIndex(index); // 🔁 isso aqui vai como props pro filho
      setHandleClick(true); // ativa o efeito no PositionsMap
      setCreateMap(true);
    }
  };

  const onHandled = () => setHandleClick(false);

  const chooseDisplay = (activeTab, selectedVehicleId) => {
    setStartInterval(false);
    setVehicleToTrack(null); // TODO: usa?
    setShowTrackEvents(false); // TODO: usa?

    setTimeout(async () => {
      setActiveTab(activeTab);

      if (activeTab === "map") {
        setMapLoading(false);
        setCreateMap(true);
        setStartInterval(true);
        await fetchTrackingList(selectedVehicleId, true);
      } else if (activeTab === "grid") {
        setMapLoading(false);
        setDataLoading(true);
        setCreateMap(false);
        if (paginatedTrackingList.length == 0)
          await fetchTrackingList(0, false);
      } else if (activeTab === "positions") {
        setMapLoading(false);
        setDataLoading(true);
        setCreateMap(true);
      } else if (activeTab === "map-positions") {
        setMapLoading(true);
        setCreateMap(true);
      } else if (activeTab === "commands") {
        setMapLoading(false);
        setDataLoading(true);
        setCreateMap(false);
      } else if (activeTab === "maintenace") {
        setMapLoading(false);
        setDataLoading(true);
        setCreateMap(false);
      } else if (activeTab === "gridAndMap") {
        setMapLoading(false);
        setCreateMap(true);
        setDataLoading(false);
        setStartInterval(true);
        if (paginatedTrackingList.length == 0)
          await fetchTrackingList(0, false);
      }
    }, 400);
  };

  const handleSelectedVehicle = (selectedVehicle) => {
    handleActiveTab("map", selectedVehicle);
  };

  const handlePositionTrack = (vehicleToTrack) => {
    console.log("Clicado em handlePositionTrack");
    setMapLoading(true);
    setStartInterval(false);

    setShowTrackEvents(true);

    setVehicleToTrack(vehicleToTrack); // Estado de veiculo para em positions tracks map

    dispatch({ type: "vehicleSelected", value: vehicleToTrack });
  };

  const handleShowPositionMap = (positions) => {
    setDiaryPositions(positions);
    // handleActiveTab("map-positions", vehicleSelected);
    handleActiveTab("positions", vehicleSelected);
  };

  const handleOnlyPositionEvents = (onlyEvents, vehicle) => {
    setOnlyPositionEvents(onlyEvents);
    //setVehicleId(vehicleId);
    handleActiveTab("positions", vehicle);
  };

  const handleTwoClick = (vehicle) => {
    dispatch({ type: "vehicleSelected", value: vehicle });
    handleOnlyPositionEvents(false, vehicle);
    setUnderMaintenace(vehicle.underMaintenance);
  };

  const reloadGrid = async () => {
    await fetchTrackingList(0, false);
  };

  const handleGridPageChange = async (newPage) => {
    currentGridPageRef.current = newPage;
    await fetchTrackingList(0, false, newPage);
  };

  const openCommandModal = async (vehicleId) => {
    try {
      const result = await vehicleService.fetchVehicleCommands(vehicleId);
      if (result && result.length > 0) {
        setAvailableCommands(result);
        setVehicleIdToCommands(vehicleId);
        setShowAvailableCommandsModal(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const closeCommandModal = () => {
    setAvailableCommands([{}]);
    setVehicleIdToCommands(0);
    setShowAvailableCommandsModal(false);
  };

  const sendVehicleCommand = async (command) => {
    try {
      const payload = {
        vehicleId: vehicleIdToCommands,
        commandId: command.commandId,
        parameters: null,
      };

      const response = await vehicleService.sendCommand(payload);

      if (!response.error) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: `Comando enviado`,
            body: command.descriptionCommand,
            color: "primary",
          })
        );
      } else {
        setToastParams(
          JSON.stringify({
            show: true,
            title: `Falha ao enviar comando`,
            body: command.descriptionCommand,
            color: "danger",
          })
        );
      }
    } catch (error) {
      console.error(error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: `Falha ao enviar comando`,
          body: command.descriptionCommand,
          color: "danger",
        })
      );
    }
  };

  // Setar titulo da tela
  useEffect(() => {
    dispatch({ type: "title", value: t("TITLE.MY_FLEET") });
  }, [dispatch]);

  // Executa ao entrar na tela (carregando os dados)
  useEffect(() => {
    (async () => {
      try {
        await fetchTrackingList(0, true);
        setActiveTab("gridAndMap");
      } catch (error) {
        console.error("Erro ao buscar os dados de rastreamento:", error);
      }
    })();
  }, []);

  // Buscar por polling
  useEffect(() => {
    if (!startInterval) return;

    const startPolling = () => {
      intervalIdRef.current = setInterval(async () => {
        console.log("🔄 Polling iniciado...");
        await fetchTrackingList(0, false);
      }, delay);
    };

    startPolling();

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [startInterval]);

  const isMapVisible = activeAccordionKeys.includes("0");
  const isGridVisible = activeAccordionKeys.includes("1");

  return (
    <>
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <Modal show={showAvailableCommandsModal} onHide={closeCommandModal}>
        <Modal.Header closeButton>
          <Modal.Title>Comandos disponíveis</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              margin: 8,
              width: "100%",
            }}
          >
            <table className="table table-grid" id="paginated-table">
              <thead>
                <tr>
                  <th key={0}>Comando</th>
                  <th key={0}>Enviar</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {availableCommands?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.descriptionCommand}</td>
                    <td>
                      <AiOutlineSend
                        cursor="pointer"
                        onClick={() => {
                          sendVehicleCommand(item);
                          closeCommandModal();
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="nav-align-top">
          <>
            <div className="row">
              {/* -- Grid -- */}

              <div
                className={`tab-pane fade  ${activeTab == "grid" ? "active show" : ""
                  }`}
                style={activeTab == "grid" ? {} : { display: "none" }}
                id="navs-pills-top-data"
                role="tabpanel"
              ></div>

              {/* -- Mapa -- */}

              <div
                className={`tab-pane fade  ${activeTab == "gridAndMap" || activeTab == "map"
                    ? "active show"
                    : ""
                  }`}
                style={
                  activeTab == "gridAndMap" || activeTab == "map"
                    ? {}
                    : { display: "none" }
                }
                id="navs-pills-top-profile"
                role="tabpanel"
              >
                {activeTab === "map" ? (
                  !showTrackEvents ? (
                    <>
                      <Suspense fallback={<Spinner />}>
                        <GoogleMapComponent
                          mapLoading={mapLoading}
                          locations={locationsTrackingList}
                          activeTab={activeTab}
                          createMap={createMap}
                          isMapVisible={isMapVisible}
                          isGridVisible={isGridVisible}
                          handleOnlyPositionEvents={handleOnlyPositionEvents}
                          handlePositionTrack={handlePositionTrack}
                          chooseDisplay={chooseDisplay}
                        />
                      </Suspense>
                    </>
                  ) : (
                    <>
                      <Suspense fallback={<Spinner />}>
                        <GoogleMapTrackComponent
                          mapLoading={mapLoading}
                          chooseDisplay={chooseDisplay}
                          vehicle={vehicleToTrack}
                        />
                      </Suspense>
                    </>
                  )
                ) : null}
              </div>

              <div
                className={`tab-pane fade ${activeTab === "map-positions" ? "active show" : ""
                  }`}
                style={activeTab === "map-positions" ? {} : { display: "none" }}
                id="navs-pills-top-map-positions"
                role="tabpanel"
              >
                <Suspense fallback={<Spinner />}>
                  <PositionsMap
                    activeTab={activeTab}
                    mapLoading={mapLoading}
                    handleActiveTab={chooseDisplay}
                    diaryPositions={diaryPositions}
                    chooseDisplay={chooseDisplay}
                  />
                </Suspense>
              </div>

              {/* -- Posições -- */}

              <div
                className={`tab-pane fade  ${activeTab == "positions" ? "active show" : ""
                  }`}
                style={activeTab == "positions" ? {} : { display: "none" }}
                id="navs-pills-top-profile"
                role="tabpanel"
              >
                <Suspense fallback={<Spinner />}>
                  <PaginatedTableFleetPositions
                    activeTab={activeTab}
                    diaryPositions={diaryPositions}
                    mapLoading={mapLoading}
                    chooseDisplay={chooseDisplay}
                    onlyEvents={onlyPositionEvents}
                    handleShowPositionMap={handleShowPositionMap}
                    underMaintenace={underMaintenace}
                    setCommandDates={handleSetCommandDates}
                    columns={[
                      "Data/Hora",
                      "ST",
                      "IG",
                      "VE",
                      "Endereço",
                      "Eventos",
                    ]}
                    fields={[
                      "generationDate",
                      "statusDescription",
                      "ignition",
                      "speed",
                      "location",
                      "levelEvent",
                    ]}
                  />
                </Suspense>
              </div>

              {/* -- Comandos -- */}

              <div
                className={`tab-pane fade  ${activeTab == "commands" ? "active show" : ""
                  }`}
                style={activeTab == "commands" ? {} : { display: "none" }}
                id="navs-pills-top-profile"
                role="tabpanel"
              >
                <Suspense fallback={<Spinner />}>
                  <PaginatedTableFleetCommands
                    activeTab={activeTab}
                    chooseDisplay={chooseDisplay}
                    onlyEvents={onlyPositionEvents}
                    handleShowPositionMap={handleShowPositionMap}
                    selectedVehicle={vehicleSelected}
                    startCommandsDate={startCommandsDate}
                    endCommandsDate={endCommandsDate}
                    columns={[
                      "Data/Hora Envio",
                      "Comando",
                      "Data/Hora Recebido",
                      "Status",
                    ]}
                    fields={[
                      "generatedAt",
                      "command",
                      "finalizedAt",
                      "statusMessage",
                    ]}
                  />
                </Suspense>
              </div>

              {/* -- Grid + Mapa -- */}

              <div
                className={`grid-and-map-component tab-pane fade  ${activeTab == "gridAndMap" || activeTab == "grid" ? "active show" : ""
                  }`}
                style={activeTab == "gridAndMap" || activeTab == "grid" ? {} : { display: "none" }}
                id="navs-pills-top-profile"
                role="tabpanel"
              >
                <Accordion
                  className="custom-accordion"
                  alwaysOpen
                  defaultActiveKey={activeAccordionKeys}
                  onSelect={(rawKey) => {
                    const keys =
                      rawKey?.toString().split(",").filter(Boolean) || [];
                    setActiveAccordionKeys(keys);

                    setTimeout(() => {
                      window.dispatchEvent(new Event("accordionToggle"));
                    }, 100);
                  }}
                >
                  {/* --- Item Mapa --- */}
                  <Accordion.Item eventKey="0" style={activeTab === "grid" ? { display: "none" } : {}}>
                    <Accordion.Header
                      onClick={() => {
                        setTimeout(() => {
                          window.dispatchEvent(new Event("accordionToggle"));
                        }, 300);
                      }}
                    >
                      <div className="d-flex justify-content-between w-100">
                        <span className="title-info">
                          {t("TITLE.MAP")}
                          <TooltipIcon
                            tooltipKey={t("MINHA_FROTA")}
                            placement="bottom"
                          />
                        </span>
                        <span className="accordion-extra-label">
                          {activeAccordionKeys.includes("0")
                            ? t("COMMON.MIN")
                            : t("COMMON.MAX")}
                        </span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {activeTab === "gridAndMap" ? (
                        !showTrackEvents ? (
                          <Suspense fallback={<Spinner />}>
                            <GoogleMapComponent
                              id="googleMapComponent"
                              mapLoading={mapLoading}
                              locations={locationsTrackingList}
                              createMap={createMap}
                              handleOnlyPositionEvents={
                                handleOnlyPositionEvents
                              }
                              handleClick={handleClick}
                              selectedIndex={selectedIndex}
                              onHandled={onHandled}
                              activeTab={activeTab}
                              isMapVisible={isMapVisible}
                              isGridVisible={isGridVisible}
                              handlePositionTrack={handlePositionTrack}
                              chooseDisplay={chooseDisplay}
                            />
                          </Suspense>
                        ) : (
                          <Suspense fallback={<Spinner />}>
                            <GoogleMapTrackComponent
                              activeTab={activeTab}
                              mapLoading={mapLoading}
                              chooseDisplay={chooseDisplay}
                              vehicle={vehicleToTrack}
                            />
                          </Suspense>
                        )
                      ) : null}
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* --- Item Grid --- */}
                  <Accordion.Item eventKey="1">
                    <Accordion.Header
                      onClick={() => {
                        setTimeout(() => {
                          window.dispatchEvent(new Event("accordionToggle"));
                        }, 300);
                      }}
                    >
                      <div className="d-flex justify-content-between w-100">
                        <span className="title-info">
                          {t("TITLE.MAIN_GRID")}
                          <TooltipIcon
                            tooltipKey={t("MINHA_FROTA")}
                            placement="bottom"
                          />
                        </span>
                        <span className="accordion-extra-label">
                          {activeAccordionKeys.includes("1")
                            ? t("COMMON.MIN")
                            : t("COMMON.MAX")}
                        </span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="card-grid-component">
                        <div className="grid-component p-1">
                          {/* <div className="grid-filters">
                            <InputFilter
                              placeholder={"Pesquisar por placa ou motorista"}
                              type="text"
                              icon={IoMdSearch}
                              width={300}
                            />
                            <div className="">
                              <Filters
                                title={"Filtrar placas"}
                                hasSubTitle={false}
                                options={[]}
                                icon={HiOutlineTruck}
                                hasIcon={true}
                              />
                            </div>
                          </div> */}

                          <Suspense fallback={<Spinner />}>
                            <PaginatedTableFleet
                              id="PaginatedTableFleetAccordion"
                              activeTab={activeTab}
                              selectedVehicle={vehicleSelected}
                              dataLoading={dataLoading}
                              data={paginatedTrackingList.vehiclesUser}
                              totalRecords={gridPaginationMeta.totalLines}
                              onPageChange={handleGridPageChange}
                              handleSelectedVehicle={handleSelectedVehicle}
                              handleTwoClick={handleTwoClick}
                              isGridVisible={isGridVisible}
                              isMapVisible={isMapVisible}
                              reloadGrid={reloadGrid}
                              chooseDisplay={chooseDisplay}
                              triggerClick={triggerClick}
                              openCommandModal={openCommandModal}
                              columns={[
                                "Placa",
                                "Grupo",
                                "Data/Hora",
                                "ST",
                                "IG",
                                "V",
                                "M",
                                "Localização",
                                ...(activeTab === "grid" ? ["Endereço"] : []),
                              ]}
                              fields={[
                                "plate",
                                "vehicleGroupName",
                                "generatedAt",
                                "colorFilter",
                                "ignition",
                                "speedStatusFilter",
                                "userDriverName",
                                "location",
                                ...(activeTab === "grid" ? ["location"] : []),
                              ]}
                            />
                          </Suspense>
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            </div>
          </>
        </div>
      </div>
    </>
  );
}
